// DME Prior Auth — orders awaiting or with active prior authorisation
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdvanceOrderModal from '../../components/dme/AdvanceOrderModal';
import { nextStatus, statusInfo } from '../../lib/dmeWorkflow';
import { format, differenceInDays } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import { isClosedClient } from '../../lib/serviceModel';


export default function DmeAuth() {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Show all orders with auth info: pending auth OR approved with upcoming expiry
    const { data } = await supabase
      .from('dme_orders')
      .select('*, clients(current_status)')
      .eq('is_test', false)
      .or(`status.in.(referral_received,auth_pending,auth_approved),auth_end_date.not.is.null`)
      .order('created_at', { ascending: false });
    // Closed clients (discharged, on hold, …) are out of authorization monitoring.
    setOrders((data || []).filter(o => !isClosedClient(o.clients)));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    if (q && !o.client_name?.toLowerCase().includes(q) && !o.order_ref?.toLowerCase().includes(q)) return false;
    if (filterStatus && o.status !== filterStatus) return false;
    return true;
  });

  const counts = {
    referral:    orders.filter(o => o.status === 'referral_received').length,
    pending:     orders.filter(o => o.status === 'auth_pending').length,
    approved:    orders.filter(o => o.status === 'auth_approved').length,
    expiring30:  orders.filter(o => {
      if (!o.auth_end_date) return false;
      const days = differenceInDays(new Date(o.auth_end_date), new Date());
      return days >= 0 && days <= 30;
    }).length,
  };


  // Product category is a parallel identifier, not a status — neutral for all.
  const catColor = () => 'var(--text3)';
  const catBg    = () => 'var(--bg2)';

  const statusBadge = (status, authEnd) => {
    if (status === 'referral_received') return { label: 'New Referral', color: 'var(--text3)', bg: 'var(--bg2)' };
    if (status === 'auth_pending')      return { label: 'Auth Pending', color: 'var(--warn)', bg: 'var(--warn-bg)' };
    if (status === 'auth_approved') {
      if (authEnd) {
        const days = differenceInDays(new Date(authEnd), new Date());
        if (days < 0)   return { label: 'Auth Expired', color: 'var(--danger)', bg: 'var(--danger-bg)' };
        if (days <= 30) return { label: `Expires in ${days}d`, color: 'var(--warn)', bg: 'var(--warn-bg)' };
      }
      return { label: 'Auth Approved', color: 'var(--success)', bg: 'var(--success-bg)' };
    }
    return { label: status, color: 'var(--text3)', bg: 'var(--bg2)' };
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };

  const columns = [
    {
      accessorKey: 'order_ref', header: 'Order', size: 110,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'client_name', header: 'Patient', size: 150,
      meta: { mobile: 'primary' },
      cell: info => info.getValue(),
    },
    {
      id: 'category', accessorKey: 'product_category', header: 'Category', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => {
        const cat = info.getValue();
        return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: catBg(cat), color: catColor(cat), textTransform: 'capitalize' }}>{cat}</span>;
      },
    },
    {
      accessorKey: 'hcpcs_code', header: 'HCPCS', size: 90,
      meta: { mono: true, mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'prescribing_physician', header: 'Physician', size: 140,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() || '—'}</span>,
    },
    {
      accessorKey: 'qualifying_diagnosis', header: 'Qualifying Dx', size: 160,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ color: 'var(--text3)' }}>{info.getValue() || '—'}</span>,
    },
    {
      accessorKey: 'auth_number', header: 'Auth #', size: 130,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue() || '—',
    },
    {
      id: 'auth_period', header: 'Auth Period', size: 170,
      meta: { mobile: 'hidden' },
      cell: info => {
        const o = info.row.original;
        return o.auth_start_date && o.auth_end_date
          ? `${format(new Date(o.auth_start_date), 'MMM d')} – ${format(new Date(o.auth_end_date), 'MMM d, yyyy')}`
          : '—';
      },
    },
    {
      id: 'status', header: 'Status', size: 130,
      meta: { mobile: 'primary' },
      cell: info => {
        const o = info.row.original;
        const sb = statusBadge(o.status, o.auth_end_date);
        return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: sb.bg, color: sb.color, whiteSpace: 'nowrap' }}>{sb.label}</span>;
      },
    },
    {
      id: 'actions', header: '', size: 190, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const o = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            {['referral_received', 'auth_pending'].includes(o.status) && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => setSelected(o)}>
                → {statusInfo(nextStatus(o.status)).label}
              </button>
            )}
            {o.status === 'auth_approved' && (
              <span style={{ fontSize: 10, color: 'var(--success)' }}>✓ {o.auth_number}</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Prior Auth (DME)</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Order-level prior authorisation for all DME product categories</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'New Referrals', val: counts.referral,   tone: 'neutral' },
          { label: 'Auth Pending',  val: counts.pending,    tone: counts.pending > 0 ? 'warn' : 'neutral' },
          { label: 'Auth Approved', val: counts.approved,   tone: 'neutral' },
          { label: 'Expiring ≤30d', val: counts.expiring30, tone: counts.expiring30 > 0 ? 'warn' : 'neutral' },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.val} tone={s.tone} compact />
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient or order ref…"
          style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 300 }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, flex: '0 0 auto', width: 'auto' }}>
          <option value="">All auth stages</option>
          <option value="referral_received">New Referral</option>
          <option value="auth_pending">Auth Pending</option>
          <option value="auth_approved">Auth Approved</option>
        </select>
      </div>

      <div className="card">
        {!loading && filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '60px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🔐</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>No authorisations pending</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>New orders appear here as soon as they are created from Equipment Orders.</div>
          </div>
        ) : (
          <DataTable
            tableId="dme-auth"
            columns={columns}
            data={filtered}
            getRowId={o => o.id}
            loading={loading}
            emptyMessage="No orders match your filters."
            maxHeight="calc(100vh - 260px)"
          />
        )}
      </div>

      {/* Record auth modal */}
      {selected && (
        <AdvanceOrderModal order={selected} onClose={() => setSelected(null)} onAdvanced={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}
